/**
 * useSgpResultFacets — derived filter facets from fetched results.
 * Extracted verbatim from SgpFlowScreen.jsx (god-file decomposition).
 * Owns: available sportsbooks/markets/players/leg-counts, team-vs-props flags,
 * SGP detection, and player-headshot enrichment (results + filter chips).
 */

import { useState, useMemo, useEffect, useRef } from 'react';
import { db } from '../firebaseConfig';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { extractResultFacets } from '../utils/sgpFlowUtils';

export default function useSgpResultFacets({ fetchedOptions, setFetchedOptions, resultsContextQuery }) {
    const [playerChipHeadshots, setPlayerChipHeadshots] = useState({});

    // getMarketRelevanceToQuery moved to ../utils/sgpFlowUtils (pure).

    const facets = useMemo(
        () => extractResultFacets(fetchedOptions, resultsContextQuery),
        [fetchedOptions, resultsContextQuery]
    );
    const {
        availableSportsbooks,
        availableMarkets,
        availablePlayers,
        availableLegCounts,
        hasTeamResults,
        hasPropsResults,
        isSgpResults,
    } = facets;

    // ─── Frontend headshot enrichment ──────────────────────────────────────────
    // The backend enriches headshots at query time, but can miss players when name
    // formats differ between player_props_cache and players_cache. As a fallback,
    // the frontend queries players_cache directly for any options still missing
    // a headshot after the backend response lands.
    const _lastEnrichedRef = useRef(null); // tracks the options array we last enriched
    useEffect(() => {
        // Skip if this is the exact same reference we already processed
        if (!fetchedOptions?.length || fetchedOptions === _lastEnrichedRef.current) return;
        const missing = fetchedOptions.filter(
            o => (o.player_name || o.raw?.player_name) && !o._player_headshot && !o._headshot_url
        );
        if (missing.length === 0) return;
        _lastEnrichedRef.current = fetchedOptions; // mark before async to prevent duplicate runs
        const uniqueNames = [...new Set(
            missing.map(o => o.player_name || o.raw?.player_name).filter(Boolean)
        )];
        (async () => {
            try {
                const headshotMap = new Map();
                for (let i = 0; i < uniqueNames.length; i += 10) {
                    const batch = uniqueNames.slice(i, i + 10);
                    const snap = await getDocs(
                        query(collection(db, 'players_cache'), where('name', 'in', batch))
                    );
                    snap.forEach(d => {
                        const data = d.data();
                        if (data.name && data.headshot_url) headshotMap.set(data.name, data.headshot_url);
                    });
                }
                if (headshotMap.size > 0) {
                    setFetchedOptions(prev => prev.map(opt => {
                        if (opt._player_headshot || opt._headshot_url) return opt;
                        const name = opt.player_name || opt.raw?.player_name;
                        if (!name) return opt;
                        const url = headshotMap.get(name);
                        return url ? { ...opt, _player_headshot: url, _headshot_url: url } : opt;
                    }));
                }
            } catch { /* non-fatal — headshots are cosmetic */ }
        })();
    }, [fetchedOptions, db]);

    // ─── Player-chip headshot enrichment ────────────────────────────────────────
    // For H2H results the main enrichment above is skipped (player_name is null).
    // When availablePlayers changes, fetch headshots for any chip players still
    // missing one and store them in playerChipHeadshots (name → url).
    useEffect(() => {
        if (!availablePlayers.length) return;
        const missing = availablePlayers
            .filter(p => !p.headshotUrl && !playerChipHeadshots[p.name])
            .map(p => p.name);
        if (missing.length === 0) return;
        (async () => {
            try {
                const headshotMap = {};
                for (let i = 0; i < missing.length; i += 10) {
                    const batch = missing.slice(i, i + 10);
                    const snap = await getDocs(
                        query(collection(db, 'players_cache'), where('name', 'in', batch))
                    );
                    snap.forEach(d => {
                        const data = d.data();
                        if (data.name && data.headshot_url) headshotMap[data.name] = data.headshot_url;
                    });
                }
                if (Object.keys(headshotMap).length > 0) {
                    setPlayerChipHeadshots(prev => ({ ...prev, ...headshotMap }));
                }
            } catch { /* non-fatal — headshots are cosmetic */ }
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availablePlayers, db]);

    return {
        availableSportsbooks,
        availableMarkets,
        availablePlayers,
        hasTeamResults,
        hasPropsResults,
        isSgpResults,
        availableLegCounts,
        playerChipHeadshots,
        setPlayerChipHeadshots,
    };
}
